import React, { memo, useMemo } from "react";

import {
  ThumbsUp,
  Cog,
  Cpu,
  Headphones,
} from "lucide-react";

import TiltedCard from "../../../components/animations/TiltedCard.jsx";
import SpotlightCard from "../../../components/animations/SpotlightCard.jsx";
import StarBorder from "../../../components/animations/StarBorder.jsx";
import Noise from "../../../components/animations/Noise.jsx";
import AnimatedHeading from "../../../components/animations/AnimatedHeading.jsx";
import GlareHover from "../../../components/animations/GlareHover.jsx";
import WavyText from "../../../components/animations/WavyText.jsx";

/* ==========================================================================
   WHY CHOOSE US DATA
============================================================================ */

const cardsData = [
  {
    icon: ThumbsUp,
    title: "Proven Track Record",
    desc:
      "With a portfolio of successful projects and satisfied clients, we have a reputation for excellence.",
  },
  {
    icon: Cog,
    title: "Tailored Solutions",
    desc:
      "Our services are customized to meet your unique business needs, ensuring a perfect fit every time.",
  },
  {
    icon: Cpu,
    title: "Future Technologies",
    desc:
      "Stay ahead of the competition with AI, cloud computing, and advanced automation solutions.",
  },
  {
    icon: Headphones,
    title: "24/7 Support",
    desc:
      "Our dedicated support team is available around the clock to ensure your business never stops.",
  },
];

/* ==========================================================================
   RESPONSIVE CARD
============================================================================ */

const Card = memo(({ Icon, title, desc }) => {
  return (
    <div className="group relative h-full">

      {/* ==================================================================
          TILT EFFECT
      =================================================================== */}

      <TiltedCard
        containerHeight="100%"
        containerWidth="100%"
        scaleOnHover={1.015}
        rotateAmplitude={3}
        edgeSoftness={0.5}
        deadZone={0.2}
        activationThreshold={8}
      >

        {/* ==================================================================
            STAR BORDER
        =================================================================== */}

        <div
          className="
            pointer-events-none
            absolute
            inset-0
            z-20
            rounded-[15px]
            opacity-0
            transition-opacity
            duration-300
            group-hover:opacity-100

            sm:rounded-[17px]
            lg:rounded-[19px]

            max-sm:hidden
          "
        >
          <StarBorder
            className="
              h-full
              w-full
              rounded-[15px]

              sm:rounded-[17px]
              lg:rounded-[19px]
            "
            color="var(--brand-primary)"
            speed="6s"
            active={false}
          />
        </div>

        {/* ==================================================================
            CARD WRAPPER
        =================================================================== */}

        <div className="relative z-10 h-full">

          <GlareHover
            width="100%"
            height="100%"
            background="transparent"
            borderRadius="16px"
            borderColor="transparent"
            glareColor="var(--brand-primary)"
            glareOpacity={0.08}
            glareAngle={-35}
            glareSize={180}
            transitionDuration={900}
            className="
              h-full
              rounded-[15px]

              sm:rounded-[17px]
              lg:rounded-[19px]

              max-sm:[&>*]:!transform-none
            "
          >

            {/* ==============================================================
                SPOTLIGHT CARD
            =============================================================== */}

            <SpotlightCard
              className="
                relative
                h-full
                overflow-hidden

                rounded-[15px]

                border
                border-theme-border

                !bg-[var(--card-bg)]
                !text-[var(--text-primary)]

                px-4
                py-4

                shadow-[0_10px_30px_var(--shadow-color)]

                transition-all
                duration-300
                ease-out

                group-hover:!bg-[#f1f1f1]
                group-hover:border-[rgba(0,0,0,0.14)]
                group-hover:shadow-[0_18px_45px_rgba(0,0,0,0.10)]

                sm:rounded-[17px]
                sm:px-5
                sm:py-5

                md:px-5
                md:py-5

                lg:rounded-[19px]
                lg:px-5
                lg:py-5

                xl:px-6
                xl:py-6

                dark:group-hover:!bg-[#111111]
                dark:group-hover:border-orange-500/30
                dark:group-hover:shadow-[0_18px_45px_rgba(0,0,0,0.30)]
              "
              tailwindColor="#E67000"
              alpha={0.04}
              radius={300}
            >

              {/* ============================================================
                  LIGHT MODE HOVER OVERLAY

                  This creates the soft white/gray hover effect.
              ============================================================= */}

              <div
                className="
                  pointer-events-none
                  absolute
                  inset-0
                  z-0

                  rounded-[inherit]

                  bg-gradient-to-br
                  from-white/0
                  via-white/0
                  to-gray-100/0

                  opacity-0

                  transition-all
                  duration-500
                  ease-out

                  group-hover:from-white/70
                  group-hover:via-gray-100/35
                  group-hover:to-gray-200/50
                  group-hover:opacity-100

                  dark:group-hover:from-white/0
                  dark:group-hover:via-white/0
                  dark:group-hover:to-white/0
                  dark:group-hover:opacity-0
                "
              />

              {/* ============================================================
                  SOFT TOP GLOW
              ============================================================= */}

              <div
                className="
                  pointer-events-none
                  absolute
                  -right-20
                  -top-20
                  z-0

                  h-40
                  w-40

                  rounded-full

                  bg-white/0
                  blur-3xl

                  transition-all
                  duration-500

                  group-hover:bg-white/70

                  dark:group-hover:bg-transparent
                "
              />

              {/* ============================================================
                  CARD CONTENT
              ============================================================= */}

              <div className="relative z-10 flex h-full flex-col items-start">

                {/* ==========================================================
                    ICON
                =========================================================== */}

                <div
                  className="
                    flex
                    h-10
                    w-10
                    shrink-0
                    items-center
                    justify-center

                    rounded-xl

                    border
                    border-[rgba(249,115,22,0.25)]

                    bg-[rgba(249,115,22,0.08)]

                    text-[var(--brand-primary)]

                    ring-1
                    ring-transparent

                    transition-all
                    duration-300
                    ease-out

                    group-hover:border-[rgba(249,115,22,0.45)]
                    group-hover:bg-[rgba(249,115,22,0.12)]
                    group-hover:ring-[rgba(249,115,22,0.15)]
                    group-hover:shadow-[0_0_22px_rgba(249,115,22,0.10)]

                    sm:h-11
                    sm:w-11

                    md:h-12
                    md:w-12

                    lg:h-[50px]
                    lg:w-[50px]
                  "
                >
                  <Icon
                    className="
                      h-[18px]
                      w-[18px]

                      sm:h-5
                      sm:w-5

                      md:h-[21px]
                      md:w-[21px]

                      lg:h-[22px]
                      lg:w-[22px]
                    "
                    strokeWidth={1.8}
                  />
                </div>

                {/* ==========================================================
                    TEXT
                =========================================================== */}

                <div
                  className="
                    mt-4
                    w-full

                    sm:mt-4

                    md:mt-[18px]
                  "
                >

                  {/* ========================================================
                      TITLE
                  ========================================================= */}

                  <h3
                    className="
                      text-[16px]
                      font-bold
                      leading-tight
                      tracking-[-0.02em]

                      text-[var(--text-primary)]

                      transition-colors
                      duration-300

                      group-hover:text-[var(--text-primary)]

                      sm:text-[17px]

                      md:text-[18px]

                      lg:text-[19px]

                      xl:text-[20px]
                    "
                  >
                    <WavyText text={title} />
                  </h3>

                  {/* ========================================================
                      DESCRIPTION
                  ========================================================= */}

                  <p
                    className="
                      mt-2
                      max-w-[360px]

                      text-[10px]
                      font-medium
                      leading-[1.6]

                      text-[var(--text-muted)]

                      transition-colors
                      duration-300

                      group-hover:text-[var(--text-secondary)]

                      sm:mt-2.5
                      sm:text-[11px]
                      sm:leading-[1.65]

                      md:text-[12px]

                      lg:text-[13px]

                      xl:text-sm
                      xl:leading-[1.65]
                    "
                  >
                    {desc}
                  </p>
                </div>

                {/* ==========================================================
                    BOTTOM ACCENT
                =========================================================== */}

                <div
                  className="
                    mt-auto

                    h-[2px]
                    w-0

                    rounded-full

                    bg-[var(--brand-primary)]

                    opacity-70

                    transition-all
                    duration-500
                    ease-out

                    group-hover:w-9

                    sm:group-hover:w-10

                    md:group-hover:w-11
                  "
                />
              </div>
            </SpotlightCard>
          </GlareHover>
        </div>
      </TiltedCard>
    </div>
  );
});

Card.displayName = "ChooseUsCard";

/* ==========================================================================
   MAIN COMPONENT
============================================================================ */

export default function ChooseUs() {
  const cards = useMemo(() => cardsData, []);

  return (
    <section
      className="
        relative
        w-full
        overflow-hidden

        bg-[var(--bg-primary)]

        font-saira

        text-[var(--text-primary)]

        px-3
        py-7

        sm:px-5
        sm:py-9

        md:px-7
        md:py-12

        lg:px-10
        lg:py-14

        xl:px-12
        xl:py-16
      "
    >

      {/* ==================================================================
          BACKGROUND ATMOSPHERE
      =================================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          inset-0
          overflow-hidden
        "
      >

        {/* ================================================================
            DESKTOP NOISE
        ================================================================= */}

        <div
          className="
            pointer-events-none
            absolute
            inset-0
            z-[2]

            hidden
            opacity-30

            md:block
          "
        >
          <Noise
            patternSize={180}
            patternScaleX={1.5}
            patternScaleY={1.5}
            patternRefreshInterval={5}
            patternAlpha={10}
          />
        </div>

        {/* ================================================================
            LEFT GLOW
        ================================================================= */}

        <div
          className="
            pointer-events-none
            absolute

            -left-32
            top-10

            hidden

            h-[200px]
            w-[200px]

            rounded-full

            bg-[rgba(249,115,22,0.045)]

            blur-[100px]

            sm:block
            sm:-left-40
            sm:h-[260px]
            sm:w-[260px]

            md:h-[320px]
            md:w-[320px]

            lg:h-[380px]
            lg:w-[380px]
          "
        />

        {/* ================================================================
            RIGHT GLOW
        ================================================================= */}

        <div
          className="
            pointer-events-none
            absolute

            -right-32
            bottom-0

            hidden

            h-[200px]
            w-[200px]

            rounded-full

            bg-[rgba(249,115,22,0.04)]

            blur-[100px]

            sm:block
            sm:-right-40
            sm:h-[260px]
            sm:w-[260px]

            md:h-[320px]
            md:w-[320px]

            lg:h-[380px]
            lg:w-[380px]
          "
        />

        {/* ================================================================
            CENTER GLOW
        ================================================================= */}

        <div
          className="
            pointer-events-none
            absolute

            left-1/2
            top-1/2

            hidden

            h-[180px]
            w-[180px]

            -translate-x-1/2
            -translate-y-1/2

            rounded-full

            bg-[rgba(249,115,22,0.02)]

            blur-[100px]

            sm:block
            sm:h-[260px]
            sm:w-[260px]

            lg:h-[360px]
            lg:w-[360px]
          "
        />
      </div>

      {/* ==================================================================
          MAIN CONTAINER
      =================================================================== */}

      <div
        className="
          relative
          z-10

          mx-auto
          w-full
          max-w-[1450px]
        "
      >

        {/* ==================================================================
            HEADER
        =================================================================== */}

        <div
          className="
            mx-auto
            mb-6
            max-w-3xl
            text-center

            sm:mb-8

            md:mb-10

            lg:mb-11
          "
        >

          {/* ================================================================
              EYEBROW
          ================================================================= */}

          <p
            className="
              text-[9px]
              font-bold
              uppercase
              tracking-[0.22em]

              text-[var(--brand-primary)]

              sm:text-[10px]
              sm:tracking-[0.25em]

              md:text-xs
              md:tracking-[0.3em]
            "
          >
            [ Why Choose Us ]
          </p>

          {/* ================================================================
              HEADING
          ================================================================= */}

          <AnimatedHeading
            as="h2"
            parts={[
              "Reliable IT Solutions, for",
              "Best Results.",
            ]}
            className="
              mt-3

              text-[1.6rem]
              font-semibold
              leading-[1.1]
              tracking-[-0.035em]

              text-[var(--text-primary)]

              sm:mt-4
              sm:text-[1.9rem]

              md:mt-5
              md:text-4xl

              lg:text-5xl

              xl:text-[3.15rem]
            "
          />

          {/* ================================================================
              DESCRIPTION
          ================================================================= */}

          <p
            className="
              mx-auto
              mt-3
              max-w-[680px]

              text-[10px]
              font-medium
              leading-[1.6]

              text-[var(--text-muted)]

              sm:mt-3.5
              sm:text-[11px]
              sm:leading-[1.7]

              md:text-[13px]

              lg:text-sm
              lg:leading-6
            "
          >
            We combine technology, creativity, and industry expertise
            to build reliable digital solutions that solve real
            business challenges and create long-term value.
          </p>
        </div>

        {/* ==================================================================
            CARDS GRID
        =================================================================== */}

        <div
          className="
            grid
            grid-cols-1
            items-stretch
            gap-3

            sm:grid-cols-2
            sm:gap-4

            md:gap-5

            lg:grid-cols-4
            lg:gap-5

            xl:gap-6
          "
        >
          {cards.map(({ icon: Icon, title, desc }) => (
            <Card
              key={title}
              Icon={Icon}
              title={title}
              desc={desc}
            />
          ))}
        </div>

        {/* ==================================================================
            BOTTOM TRUST LINE
        =================================================================== */}

        <div
          className="
            mt-6

            flex
            items-center
            justify-center
            gap-2

            text-center

            sm:mt-7

            md:mt-9
          "
        >

          <span
            className="
              h-1
              w-1
              rounded-full

              bg-[var(--brand-primary)]

              shadow-[0_0_10px_rgba(249,115,22,0.7)]
            "
          />

          <span
            className="
              text-[7px]
              font-medium
              uppercase
              tracking-[0.15em]

              text-[var(--text-muted)]

              sm:text-[8px]
              sm:tracking-[0.18em]

              md:text-[9px]
              md:tracking-[0.2em]
            "
          >
            Technology that moves your business forward
          </span>

          <span
            className="
              h-1
              w-1
              rounded-full
              bg-[var(--brand-primary)]
              shadow-[0_0_10px_rgba(249,115,22,0.7)]
            "
          />
        </div>
      </div>
    </section>
  );
}